import type {ReactElement} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {CircularProgressLoader, LOADER_SIZE, LOADER_COLOR} from '@Components/circular-progress-loader';
import {fetchSignupForms, getSignupFormsData} from '@EmailMakerReact/lib/email-maker-social-signup-library';
import type {UserSignupFormLinkData} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import styles from './signup-form-suggestions.module.scss';

interface SignupFormSuggestionsProps {
  currentUrl: string;
  isVisible: boolean;
  onSelectUrl: (url: string) => void;
}

export function SignupFormSuggestions({currentUrl, isVisible, onSelectUrl}: SignupFormSuggestionsProps): ReactElement {
  const [forms, setForms] = useState<UserSignupFormLinkData[]>(() => getSignupFormsData().signupForms);
  const [isLoading, setIsLoading] = useState(false);
  const loaderRef = useRef<HTMLLIElement>(null);
  const containerRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

  const scrollToSelected = useCallback(
    (formList: UserSignupFormLinkData[]): void => {
      const selectedIndex = formList.findIndex((f) => f.url === currentUrl);
      const container = containerRef.current;
      const selectedEl = selectedIndex >= 0 ? itemRefs.current[selectedIndex] : null;
      if (container && selectedEl) {
        container.scrollTo({
          top: selectedEl.offsetTop - container.offsetTop + selectedEl.offsetHeight * 2,
          behavior: 'smooth',
        });
      }
    },
    [currentUrl]
  );

  const loadMore = useCallback((): void => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    void fetchSignupForms((newItemsStartIndex: number) => {
      const updatedForms = getSignupFormsData().signupForms;
      setForms([...updatedForms]);
      setIsLoading(false);

      const isFirstPage = newItemsStartIndex === 0;
      if (isFirstPage) {
        scrollToSelected(updatedForms);
      }
    });
  }, [isLoading, scrollToSelected]);

  useEffect(() => {
    const loader = loaderRef.current;
    const container = containerRef.current;

    if (!loader || !container) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadMore();
          }
        });
      },
      {root: container, threshold: 1.0}
    );

    observer.observe(loader);

    return (): void => {
      observer.disconnect();
    };
  }, [loadMore]);

  function renderLoader(): ReactElement | null {
    if (!isLoading) {
      return null;
    }
    return <CircularProgressLoader size={LOADER_SIZE.DEFAULT} color={LOADER_COLOR.PRIMARY_DARK} />;
  }

  if (!isVisible) {
    return <></>;
  }

  return (
    <div className={styles.dropdown}>
      <ul className={styles.list} ref={containerRef}>
        <li className={styles.header}>
          <Text val={window.i18next.t('pmwjs_suggested_links')} size={TextSize.SM} bold />
        </li>
        {forms.map((form, index) => (
          <li
            className={`${styles.listItem} ${form.url === currentUrl ? '-selected' : ''}`}
            key={form.url}
            ref={(el): void => {
              itemRefs.current[index] = el;
            }}
            title={form.name}
          >
            <button
              className={styles.link}
              onClick={(): void => {
                onSelectUrl(form.url);
              }}
              type="button"
            >
              <div className={styles.iconContainer}>
                <Icon aria-hidden icon="icon-signup-form" size={IconSize.SIZE_ICON_24} />
              </div>
              <div className={styles.textContainer}>
                <Text val={form.name} size={TextSize.MD} bold className={styles.heading} />
                <Text val={window.i18next.t('pmwjs_signup_form')} size={TextSize.SM} className={styles.description} />
              </div>
            </button>
          </li>
        ))}
        <li className={styles.loader} ref={loaderRef}>
          {renderLoader()}
        </li>
      </ul>
    </div>
  );
}
